// Newsletter area dictionary — studio, campaign/edition editors, queue, results,
// segments, subscribers. Email CONTENT defaults (sent to customers) are NOT here
// on purpose — only admin UI strings.
export const newsletter = {
  shared: {
    customersCount_one: "{{count}} customer",
    customersCount_few: "{{count}} customers",
    customersCount_other: "{{count}} customers",
    failedCount_one: "{{count}} failed",
    failedCount_few: "{{count}} failed",
    failedCount_other: "{{count}} failed",
    sendingToast: "Sending",
  },
  campaignEditor: {
    titleNew: "New campaign",
    titleEdit: "Edit campaign",
    statusSent: "Sent",
    statusScheduled: "Scheduled",
    statusDraft: "Draft",
    sendTest: "Send test",
    toneOfVoice: "Tone of voice",
    toneFriendly: "Friendly",
    toneFormal: "Formal",
    toneExcited: "Excited",
    themeOptional: "Theme (optional)",
    themePlaceholder: "e.g. Summer vacation, Adventure, Romance...",
    generating: "Generating...",
    generateContent: "Generate content",
    emailSubject: "Email subject",
    subjectPlaceholder: "New trips to exotic destinations",
    previewTextLabel: "Preview text",
    previewTextPlaceholder: "Short text shown in email preview...",
    charCount: "{{n}}/150 characters",
    aiGenerationTitle: "AI content generation",
    aiGenerationHint: "Select trips and let AI generate the newsletter",
    generateWithAi: "Generate with AI",
    emailContent: "Email content",
    contentPlaceholder: "Your newsletter content...",
    scheduleSending: "Schedule sending",
    schedule: "Schedule",
    targetAudience: "Target audience",
    changeSegment: "Change",
    selectSegment: "Select segment",
    allSubscribers: "All newsletter subscribers",
    orSelectSegment: "Or select an AI segment",
    selectSegmentColon: "Select segment:",
    noSegments: "No segments.",
    createNew: "Create new",
    tripsInNewsletter: "Trips in newsletter ({{n}})",
    daysCount_one: "{{count}} day",
    daysCount_few: "{{count}} days",
    daysCount_other: "{{count}} days",
    noActiveTrips: "No active trips",
    emailPreview: "Email preview",
    subjectColon: "Subject:",
    noSubject: "(no subject)",
    previewColon: "Preview:",
    emptyContent: "Empty content...",
    alreadySent: "This campaign has already been sent and cannot be edited.",
    toastCreated: "Campaign created",
    toastCreateFailed: "Failed to create campaign",
    toastSaved: "Campaign saved",
    toastSaveFailed: "Failed to save campaign",
    toastGenerated: "Content generated with AI",
    toastGenerateFailed: "Failed to generate content",
    toastSent: "Campaign sent",
    toastSendFailed: "Failed to send campaign",
    toastTestSent: "Test sent",
    toastTestFailed: "Test send failed",
    saveFirst: "Save the campaign first",
    testPrompt: "Send a test to which email? (blank = your own)",
    errorMissingId: "Failed to create campaign — missing ID",
    errorCampaignIdMissing: "Campaign ID missing",
  },
  editionEditor: {
    title: "Edition editor",
    aiDraftPrefix: "AI draft — ",
    previewFailed: "Preview failed",
    rewritten: "Rewritten",
    rewriteFailed: "Rewrite failed",
    saved: "Edition saved",
    saveFailed: "Save failed",
    notFound: "Campaign not found.",
    regenerate: "Regenerate",
    regenerateTitle: "Regenerate draft copy",
    sendEllipsis: "Send…",
    previewIframeTitle: "Newsletter preview",
    renderingPreview: "Rendering preview…",
    subject: "Subject",
    preheader: "Preheader",
    abVariants: "A/B subject variants (wave 1 test)",
    globalInstructionPlaceholder:
      "Global instruction — e.g. 'shorter, punchier, less salesy' (locked sections untouched)",
    lockedChip: "locked",
    unlockTitle: "Unlock — AI may edit again",
    lockTitle: "Lock — AI never touches it",
    sectionPromptPlaceholder: "Tell the AI what to change in this section…",
  },
  sendDialog: {
    title: "Send campaign",
    eligible: "eligible",
    unsubscribed: "unsubscribed",
    freqCapped: "freq-capped",
    paused: "paused",
    dormant: "dormant",
    basis: "Basis: {{explicit}} subscribed · {{customer}} customers · {{lead}} leads.",
    abTestInfo: "Subject A/B test: wave 1 = 25 %, winner by clicks rolls out ~{{hours}} h later.",
    singleWave:
      "Single wave — a subject A/B test needs 2+ subject variants and a big enough audience.",
    narrowToSegment: "Narrow to segment (optional)",
    everyoneEligible: "Everyone eligible",
    sendTestTo: "Send test to",
    testEmailPlaceholder: "yourself (default: your login email)",
    scheduleOptional: "Schedule (optional — leave empty to send now)",
    whyThisDraft: "Why this draft? (optional — feeds the learning loop)",
    whyPlaceholder: "e.g. strongest hook, fits the Tet angle…",
    sendNow: "Send now",
    confirmSchedule: "Schedule this campaign for {{time}}?",
    confirmSendNow: "Send to {{n}} contacts now?",
    testSentToast: "Test sent — check your inbox",
    testFailed: "Test failed",
    sendFailed: "Send failed",
  },
  studio: {
    title: "Newsletter studio",
    subtitle: "AI drafts every Monday — you pick, polish and send.",
    tabs: {
      drafts: "Drafts",
      queue: "Queue",
      directives: "Directives",
      personas: "Personas",
      voice: "Brand voice",
      calendar: "Calendar",
      results: "Results",
      subscribers: "Subscribers",
      segments: "Segments",
    },
  },
  generate: {
    started: "Generation started — drafts in a few minutes",
    startFailed: "Failed to start",
    generateNow: "Generate now",
    directiveLabel: "Directive (optional)",
    directivePlaceholder: "e.g. 'focus on Tet departures' or a theme for this edition",
    mustIncludeLabel: "Must-include trips (optional, max {{max}})",
    mustIncludePlaceholder: "Search published trips...",
    mustIncludeHint: "Selected trips are guaranteed to appear first in every draft.",
    start: "Start",
    tokens: "{{n}}k tokens",
    runStatus: {
      running: "running",
      done: "done",
      failed: "failed",
      aborted: "aborted",
    },
  },
  drafts: {
    noDraftsPrefix: "No AI drafts yet — hit",
    noDraftsSuffix: "or wait for Monday 06:00 UTC.",
    olderDrafts: "Older drafts",
    manualTitle: "Manual campaigns",
    newManual: "New manual campaign",
    manualEmpty:
      "No hand-written drafts. Manual campaigns skip the AI pipeline — you write the HTML yourself; sending, scheduling and tracking work the same.",
    confirmDeleteDraft: "Delete draft “{{subject}}”?",
    deleteDraftTitle: "Delete draft",
    deleted: "Campaign deleted",
    deleteFailed: "Failed to delete campaign",
    draftFallback: "Draft",
    pickAndEdit: "Pick & edit",
  },
  directives: {
    newTitle: "New directive",
    kindInspiration: "Inspiration (nudge)",
    kindAssignment: "Assignment (must cover)",
    assignmentPlaceholder: "All 3 drafts will cover this — e.g. 'Tet 2027 departures are open'",
    inspirationPlaceholder: "A nudge for the theme step — e.g. 'mention the new Ha Giang loop'",
    verbatimPlaceholder: "Optional VERBATIM text — inserted word-for-word as a locked note section",
    add: "Add directive",
    added: "Directive added",
    listTitle: "Directives",
    kinds: {
      inspiration: "inspiration",
      assignment: "assignment",
    },
    statuses: {
      pending: "pending",
      consumed: "consumed",
      archived: "archived",
    },
    verbatimNote: "verbatim: {{text}}",
    empty: "No directives yet.",
  },
  personas: {
    newTitle: "New persona",
    namePlaceholder: "Name — e.g. 'Adventurous couple'",
    descriptionPlaceholder:
      "Who they are, what convinces them — the critique step roleplays this reader.",
    add: "Add persona",
    listTitle: "Personas",
    discovered: "discovered",
    review: "review",
    active: "active",
    empty: "No personas yet.",
  },
  voice: {
    saved: "Voice doc saved",
    reanalyzed: "Re-analyzed from historical newsletters",
    title: "Brand voice guide",
    reanalyze: "Re-analyze from history",
    hint: "The copywriter step reads this before writing every edition. Edit freely — it's yours.",
  },
  calendar: {
    from: "From",
    to: "To",
    labelPlaceholder: "Label — e.g. Tet (Lunar New Year)",
    notePlaceholder: "Note for the analyst (optional)",
    empty: "No entries — add Tet, summer holidays, best-time-to-visit windows…",
  },
  queue: {
    relIn: "in {{span}}",
    relAgo: "{{span}} ago",
    spanHM: "{{h}} h {{m}} min",
    spanM: "{{m}} min",
    nextEditionPrefix: "Next AI edition:",
    nextEditionSuffix: "(Mon 06:00 UTC)",
    sendRatePrefix: "Send rate:",
    ratePerMin: "{{n}}/min",
    wave2Prefix: "Subject-test wave 2:",
    wave2Value: "+{{n}} h",
    wave2Suffix: "after wave 1",
    scheduledHeading: "Scheduled ({{n}})",
    scheduledEmpty: "Nothing scheduled — pick a draft and use “Schedule” in the send dialog.",
    sendingHeading: "Sending now ({{n}})",
    sendingEmpty: "No campaign in flight.",
    audienceSegment: "segment “{{name}}”",
    audienceFull: "the full audience",
    confirmSendNow: "Send “{{subject}}” to {{audience}} NOW?",
    confirmCancel: "Cancel this schedule? The campaign goes back to drafts.",
    cancelled: "Schedule cancelled",
    rescheduled: "Rescheduled",
    actionFailed: "Action failed",
    segmentInfo: "segment: {{name}}",
    aiEdition: "AI edition",
    manualLabel: "manual",
    reschedule: "Reschedule",
    sendNow: "Send now",
    cancelScheduleTitle: "Cancel schedule",
    sentOfTotal: "{{sent}}/{{total}} sent",
    waveWaiting: "Subject test: wave 1 out, winner by clicks ~{{time}} → wave 2 ({{n}} waiting)",
    waveRolling: "Wave 2 rolling out with variant {{variant}}",
    queuedCount: "{{n}} queued",
    startedAt: "started {{time}}",
  },
  results: {
    retroSaved: "Retro note saved — the analyst will see it",
    retroSaveFailed: "Failed to save the note",
    colCampaign: "Campaign",
    colSent: "Sent",
    colOpens: "Opens",
    colClicks: "Clicks",
    colCtr: "CTR",
    showBreakdown: "Show click breakdown",
    retroPlaceholder: "What did we learn?",
    addRetroNote: "+ retro note",
    noSentCampaigns: "No sent campaigns yet.",
    noClicks: "No clicks yet.",
    audienceInterest: "Audience interest — clicks by destination (all campaigns)",
    mostClickedLinks: "Most clicked links",
    delivered: "Delivered",
    uniqueOpens: "unique opens",
    uniqueClickers: "unique clickers",
    housekeepingClicks_one: "{{count}} housekeeping click (unsub/view)",
    housekeepingClicks_few: "{{count}} housekeeping clicks (unsub/view)",
    housekeepingClicks_other: "{{count}} housekeeping clicks (unsub/view)",
    topLinks: "Top links",
    bySection: "By section",
    byDestination: "By destination",
  },
  sections: {
    labels: {
      bar: "Top bar",
      hero: "Hero",
      intro: "Intro",
      trustBoxes: "Trust boxes",
      spotlight: "Spotlight",
      tripCards: "Trip cards",
      review: "Review",
      included: "What's included",
      video: "Video",
      routeTimeline: "Route timeline",
      combos: "Combos",
      note: "Note",
      helpDesk: "Help desk",
      footer: "Footer",
    },
    fields: {
      left: "Left",
      right: "Right",
      eyebrow: "Eyebrow",
      accent: "Accent (highlighted)",
      title: "Title",
      subtitle: "Subtitle",
      image: "Image",
      greeting: "Greeting ({{placeholder}} allowed)",
      body: "Body",
      variant: "Variant",
      variantGuide: "Guide",
      variantBranch: "Branch",
      variantCustom: "Custom",
      bullets: "Bullets (one per line)",
      photo: "Photo",
      mode: "Mode",
      modePersonalized: "Personalized (per recipient)",
      modeManual: "Manual (these exact trips)",
      discoverySlot: "Discovery slot",
      tripsExact: "Trips (exactly these)",
      tripsFallback: "Fallback trips (no-profile recipients)",
      hookPlaceholder: "hook (one line, no numbers)",
      addTrip: "+ add trip…",
      quote: "Quote",
      author: "Author",
      context: "Context (trip)",
      linkOptional: "Link (optional)",
      includedList: "Included (one per line)",
      excludedList: "Not included (one per line)",
      youtubeUrl: "YouTube URL",
      caption: "Caption",
      thumbnail: "Thumbnail",
      titleTripName: "Title (trip name)",
      stops: "Stops (one per line, 2–8)",
      comboTitle: "Title",
      comboSubtitle: "Subtitle",
      comboPrice: "Price label",
      tone: "Tone",
      toneInfo: "Info (gold)",
      toneWarning: "Warning (accent)",
      ctaLabel: "CTA label",
      ctaUrl: "CTA URL",
      phone: "Phone",
      trustLabelPlaceholder: "LABEL",
      trustValuePlaceholder: "Value",
      footerNote:
        "Footer is fully generated at send time (unsubscribe link, address, view in browser).",
    },
  },
  themes: {
    violet: "Go2 Magenta",
    magenta: "Zine Magenta",
    jade: "Jade Coast",
    sunset: "Golden Hour",
  },
  segments: {
    deleted: "Segment deleted",
    deleteFailed: "Failed to delete segment",
    regenerated: "Segment regenerated",
    regenerateFailed: "Failed to regenerate segment",
    intro:
      'Write a segment description in natural language (e.g. "customers who traveled with us to Vietnam last year") and AI builds it automatically. Segments narrow the audience in the send dialog.',
    newSegment: "New segment",
    searchPlaceholder: "Search segments...",
    automatic: "Automatic",
    lastUpdated: "Last updated: {{date}}",
    regenerateTitle: "Regenerate customers",
    viewCustomers: "View customers",
    deleteTitle: "Delete segment",
    noSegments: "No segments",
    createFirst: "Create first segment",
    confirmDeleteMessage: "Are you sure you want to delete this segment? This cannot be undone.",
    previewFailed: "Failed to create preview",
    created: "Segment created",
    createFailed: "Failed to create segment",
    enterDescription: "Enter segment description",
    enterName: "Enter segment name",
    describeHint: "Describe the segment in natural language",
    nameLabel: "Segment name",
    namePlaceholder: "e.g. VIP customers",
    promptLabel: "Segment description (AI prompt)",
    promptPlaceholder:
      "e.g. customers who traveled with us to Vietnam last year and spent more than 50,000 CZK",
    tips: 'Tips: "customers without orders this year", "customers with birthdays this month", "customers from California who prefer Southeast Asia"',
    createSegment: "Create segment",
    previewTitle: "Segment preview",
    moreCustomers_one: "... and {{count}} more customer",
    moreCustomers_few: "... and {{count}} more customers",
    moreCustomers_other: "... and {{count}} more customers",
    noCustomers: "No customers in segment",
    totalSpent: "Total spent",
    tripsCol: "Trips",
  },
  subscribers: {
    activeWord_one: "active",
    activeWord_few: "active",
    activeWord_other: "active",
    unsubscribedWord_one: "unsubscribed",
    unsubscribedWord_few: "unsubscribed",
    unsubscribedWord_other: "unsubscribed",
    suppressedWord_one: "suppressed",
    suppressedWord_few: "suppressed",
    suppressedWord_other: "suppressed",
    pendingWord_one: "pending",
    pendingWord_few: "pending",
    pendingWord_other: "pending",
    searchPlaceholder: "Search subscribers…",
    noSubscribers: "No subscribers",
    colSource: "Source",
    colSubscribed: "Subscribed",
    statusSuppressed: "Suppressed",
    statusPending: "Pending",
    statusUnsubscribed: "Unsubscribed",
  },
};
